import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import React, { useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { Add, AssignmentTurnedIn, Edit } from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import { equipmentInventoryService } from "../../api/equipmentInventoryService";
import {
  EquipmentItem,
  SaveEquipmentItem,
} from "../../interfaces/Fleet/EquipmentInventory";
import { DriverDto } from "../../interfaces/Fleet/DriverDto";
import EquipmentInventoryLists from "../../components/Fleet/EquipmentInventory/EquipmentInventoryLists";

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });
const getError = (error: unknown) =>
  axios.isAxiosError(error)
    ? error.response?.data?.detail ||
      error.response?.data?.title ||
      Object.values(error.response?.data?.errors || {})
        .flat()
        .join(" ") ||
      error.message
    : error instanceof Error
      ? error.message
      : "Something went wrong.";

type IssueRow = {
  equipmentItemId: string;
  quantityIssued: string;
  condition: string;
};

export default function EquipmentInventoryPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [equipmentDialog, setEquipmentDialog] = useState<
    EquipmentItem | null | "new"
  >(null);
  const [equipmentForm, setEquipmentForm] = useState<SaveEquipmentItem>({
    name: "",
    totalQuantity: 0,
    unitPrice: 0,
  });
  const [issueOpen, setIssueOpen] = useState(false);
  const [driverId, setDriverId] = useState("");
  const [truckNumber, setTruckNumber] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");
  const [issueRows, setIssueRows] = useState<IssueRow[]>([
    { equipmentItemId: "", quantityIssued: "1", condition: "New" },
  ]);

  const equipment = useQuery({
    queryKey: ["equipment-inventory"],
    queryFn: () => equipmentInventoryService.getEquipment(token),
  });
  const drivers = useQuery({
    queryKey: ["equipment-inventory-drivers"],
    queryFn: async () =>
      (
        await axios.get<DriverDto[]>(`${apiVariable}/getAllDrivers`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { areActive: true },
        })
      ).data,
  });
  const saveEquipment = useMutation({
    mutationFn: () =>
      equipmentDialog === "new"
        ? equipmentInventoryService.createEquipment(token, equipmentForm)
        : equipmentInventoryService.updateEquipment(
            token,
            equipmentDialog!.id,
            equipmentForm,
          ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipment-inventory"] });
      setEquipmentDialog(null);
    },
  });
  const issue = useMutation({
    mutationFn: () =>
      equipmentInventoryService.issue(token, {
        driverId,
        truckNumber: truckNumber.trim(),
        trailerNumber: trailerNumber.trim(),
        items: issueRows.map((row) => ({
          ...row,
          quantityIssued: Number(row.quantityIssued),
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipment-inventory"] });
      queryClient.invalidateQueries({
        queryKey: ["equipment-inventory-lists"],
      });
      setIssueOpen(false);
      setTab(1);
    },
  });

  const inventoryValue =
    equipment.data?.reduce(
      (sum, item) => sum + item.totalQuantity * item.unitPrice,
      0,
    ) || 0;
  const available =
    equipment.data?.reduce((sum, item) => sum + item.availableQuantity, 0) || 0;
  const outstanding =
    equipment.data?.reduce((sum, item) => sum + item.outstandingQuantity, 0) ||
    0;
  const issueValidation = (() => {
    if (!driverId || !truckNumber.trim() || !trailerNumber.trim())
      return "Select a driver and enter truck and trailer numbers.";
    if (!issueRows.length || issueRows.some((r) => !r.equipmentItemId))
      return "Select equipment for every row.";
    if (
      new Set(issueRows.map((r) => r.equipmentItemId)).size !== issueRows.length
    )
      return "Each equipment item can only be added once.";
    for (const row of issueRows) {
      const qty = Number(row.quantityIssued);
      const item = equipment.data?.find((i) => i.id === row.equipmentItemId);
      if (!Number.isInteger(qty) || qty <= 0)
        return "Issued quantities must be positive whole numbers.";
      if (item && qty > item.availableQuantity)
        return `${item.name}: only ${item.availableQuantity} available.`;
    }
    return "";
  })();

  const columns: GridColDef[] = [
    { field: "name", headerName: "Equipment", flex: 1, minWidth: 180 },
    {
      field: "totalQuantity",
      headerName: "Owned",
      width: 100,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "availableQuantity",
      headerName: "Available",
      width: 110,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "outstandingQuantity",
      headerName: "Outstanding",
      width: 120,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "unitPrice",
      headerName: "Unit price",
      width: 120,
      valueFormatter: (value: number) => money(value),
    },
    {
      field: "outstandingValue",
      headerName: "Outstanding value",
      width: 160,
      valueFormatter: (value: number) => money(value),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          startIcon={<Edit />}
          onClick={() => {
            setEquipmentDialog(row);
            setEquipmentForm({
              name: row.name,
              totalQuantity: row.totalQuantity,
              unitPrice: row.unitPrice,
            });
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ py: 4 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Equipment Inventory
          </Typography>
          <Typography color="text.secondary">
            Manage stock, issue equipment to drivers, and record returns.
          </Typography>
        </Box>
        <Stack
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          useFlexGap
          direction="row"
          spacing={1}
        >
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              setEquipmentDialog("new");
              setEquipmentForm({ name: "", totalQuantity: 0, unitPrice: 0 });
            }}
          >
            Add equipment
          </Button>
          <Button
            variant="contained"
            startIcon={<AssignmentTurnedIn />}
            onClick={() => setIssueOpen(true)}
          >
            Create inventory list
          </Button>
        </Stack>
      </Stack>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ["Equipment types", equipment.data?.length || 0],
          ["Available units", available],
          ["Outstanding units", outstanding],
          ["Inventory value", money(inventoryValue)],
        ].map(([label, value]) => (
          <Grid item xs={6} md={3} key={label}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Equipment stock" />
        <Tab label="Inventory lists & returns" />
      </Tabs>
      {tab === 0 &&
        (equipment.isError ? (
          <Alert severity="error">{getError(equipment.error)}</Alert>
        ) : (
          <DataGrid
            rows={equipment.data || []}
            columns={columns}
            loading={equipment.isLoading}
            autoHeight
            disableRowSelectionOnClick
          />
        ))}
      {tab === 1 && <EquipmentInventoryLists />}

      <Dialog
        open={!!equipmentDialog}
        onClose={() => setEquipmentDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {equipmentDialog === "new" ? "Add equipment" : "Update equipment"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Equipment name"
              required
              value={equipmentForm.name}
              onChange={(e) =>
                setEquipmentForm({ ...equipmentForm, name: e.target.value })
              }
              inputProps={{ maxLength: 150 }}
            />
            <TextField
              label="Total quantity owned"
              required
              type="number"
              value={equipmentForm.totalQuantity}
              onChange={(e) =>
                setEquipmentForm({
                  ...equipmentForm,
                  totalQuantity: Number(e.target.value),
                })
              }
              inputProps={{ min: 0, step: 1 }}
              helperText="Whole numbers only. Quantity currently issued cannot be removed."
            />
            <TextField
              label="Price per unit"
              required
              type="number"
              value={equipmentForm.unitPrice}
              onChange={(e) =>
                setEquipmentForm({
                  ...equipmentForm,
                  unitPrice: Number(e.target.value),
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Price changes do not affect previously issued items."
            />
            {saveEquipment.isError && (
              <Alert severity="error">{getError(saveEquipment.error)}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEquipmentDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !equipmentForm.name.trim() ||
              equipmentForm.totalQuantity < 0 ||
              !Number.isInteger(equipmentForm.totalQuantity) ||
              equipmentForm.unitPrice < 0 ||
              saveEquipment.isPending
            }
            onClick={() => saveEquipment.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Create driver inventory list</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Driver</InputLabel>
                    <Select
                      value={driverId}
                      label="Driver"
                      onChange={(e) => setDriverId(e.target.value)}
                    >
                      {drivers.data?.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.firstName} {d.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Truck number"
                    value={truckNumber}
                    onChange={(e) => setTruckNumber(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Trailer number"
                    value={trailerNumber}
                    onChange={(e) => setTrailerNumber(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Equipment issued
            </Typography>
            {issueRows.map((row, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth required>
                    <InputLabel>Equipment</InputLabel>
                    <Select
                      value={row.equipmentItemId}
                      label="Equipment"
                      onChange={(e) =>
                        setIssueRows(
                          issueRows.map((r, i) =>
                            i === index
                              ? { ...r, equipmentItemId: e.target.value }
                              : r,
                          ),
                        )
                      }
                    >
                      {equipment.data
                        ?.filter((i) => i.availableQuantity > 0)
                        .map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name} — {item.availableQuantity} available
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={5} md={2}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={row.quantityIssued}
                    onChange={(e) =>
                      setIssueRows(
                        issueRows.map((r, i) =>
                          i === index
                            ? { ...r, quantityIssued: e.target.value }
                            : r,
                        ),
                      )
                    }
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>
                <Grid item xs={5} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      label="Condition"
                      value={row.condition}
                      onChange={(e) =>
                        setIssueRows(
                          issueRows.map((r, i) =>
                            i === index
                              ? { ...r, condition: e.target.value }
                              : r,
                          ),
                        )
                      }
                    >
                      {["New", "Used", "Damaged"].map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <Button
                    color="error"
                    disabled={issueRows.length === 1}
                    onClick={() =>
                      setIssueRows(issueRows.filter((_, i) => i !== index))
                    }
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}
            <Button
              sx={{ alignSelf: "flex-start" }}
              startIcon={<Add />}
              onClick={() =>
                setIssueRows([
                  ...issueRows,
                  {
                    equipmentItemId: "",
                    quantityIssued: "1",
                    condition: "New",
                  },
                ])
              }
            >
              Add item
            </Button>
            {(issueValidation || issue.isError) && (
              <Alert severity="error">
                {issue.isError ? getError(issue.error) : issueValidation}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!!issueValidation || issue.isPending}
            onClick={() => issue.mutate()}
          >
            Issue equipment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
